import { Stack, Typography } from '@mui/material'

import { AssistantSpeakerRole } from '../../../../../../../pure-js/types/AppAssistant'
import { getAriaTranscriptSpeaker, type AriaTranscriptTurn } from '../../../../../../../pure-js/libs/ariaTranscript'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { AriaBreathingBulb } from '../../../../../shared/components/AriaBreathingBulb'
import { AriaVoiceControls } from '../../../../../shared/components/AriaVoiceControls'
import { AriaSubtitles } from '../../../../../shared/components/AriaSubtitles'
import {
  COACH_BULB_SIZE_PX,
  COACH_BULB_SIZE_WITH_SUBTITLES_PX,
  COACH_SUBTITLE_MAX_WIDTH_PX,
  COACH_VOICE_BUTTON_SIZE_PX
} from '../coachPageStyles'

type CoachVoicePanelProps = {
  statusLabel: string
  live: boolean
  muted: boolean
  /** Captions are a mode, not a state — plain voice mode deliberately shows no text at all. */
  showSubtitles: boolean
  revealExistingSubtitle?: boolean
  /** This session's turns. Only Aria's are captioned; who holds the floor drives the bulb. */
  liveTurns: AriaTranscriptTurn[]
  getInputLevel: () => number
  getOutputLevel: () => number
  onStop: () => void
  onToggleMute: () => void
}

export const CoachVoicePanel = (props: CoachVoicePanelProps) => {
  const {
    statusLabel,
    live,
    muted,
    showSubtitles,
    revealExistingSubtitle = false,
    liveTurns,
    getInputLevel,
    getOutputLevel,
    onStop,
    onToggleMute
  } = props

  const isAriaSpeaking = getAriaTranscriptSpeaker(liveTurns) === AssistantSpeakerRole.ASSISTANT

  return (
    <Stack alignItems="center" justifyContent="center" spacing={1.5} sx={{ flex: 1, minHeight: 0, py: 1 }}>
      {/* Aria made visible: her own voice drives the bulb while she talks, the microphone while you do.
          Fixed size — only the captions flex, or a long answer squeezes the orb and the controls. */}
      <Stack alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
        <AriaBreathingBulb
          size={showSubtitles ? COACH_BULB_SIZE_WITH_SUBTITLES_PX : COACH_BULB_SIZE_PX}
          getLevel={isAriaSpeaking ? getOutputLevel : getInputLevel}
          isSpeaking={live && (isAriaSpeaking || !muted)}
        />
        <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 600 }}>{statusLabel}</Typography>
      </Stack>

      {showSubtitles && (
        <Stack sx={{ width: '100%', maxWidth: COACH_SUBTITLE_MAX_WIDTH_PX, flex: 1, minHeight: 0 }}>
          <AriaSubtitles turns={liveTurns} showExistingTextImmediately={revealExistingSubtitle} />
        </Stack>
      )}

      <Stack sx={{ flexShrink: 0 }}>
        <AriaVoiceControls
          muted={muted}
          onStop={onStop}
          onToggleMute={onToggleMute}
          size={COACH_VOICE_BUTTON_SIZE_PX}
        />
      </Stack>
    </Stack>
  )
}
